import { getDeviceContext } from '@lib/analytics'
import api from '@lib/api'
import type {
	ChatDiscussionKind,
	GetAccommodationResponse,
	GetChatLinkPreviewResponse,
	GetChatMessagesResponse,
	GetChatMuteResponse,
	GetChatUnreadResponse,
	GetFlightGroupResponse,
	PostChatPhotosResponse,
} from '@orbit/api-types'
import type { ChatMessage, ChatReactionChoice, PostChatMessageBody } from './core'

/*
 * A trip's Chat on the server (orbit-backend ADR 0012). Nothing is cached: a stream is
 * fetched each time its screen opens, and kept live from the trip room's `chat_updated`.
 */

const chat = (tripId: string | number) => `/trips/${tripId}/chat`

/** One page of the stream, newest first; `nextBefore` is the cursor for the next, older page. */
export const fetchMessages = async (tripId: string, before?: number | null): Promise<GetChatMessagesResponse> => {
	const { data } = await api.get<GetChatMessagesResponse>(`${chat(tripId)}/messages`, {
		params: before != null ? { before } : undefined,
	})
	return data
}

/** One page of a resource's Discussion, paged like the stream. */
export const fetchDiscussion = async (
	tripId: string,
	kind: ChatDiscussionKind,
	targetId: number,
	before?: number | null,
): Promise<GetChatMessagesResponse> => {
	const { data } = await api.get<GetChatMessagesResponse>(`${chat(tripId)}/discussions/${kind}/${targetId}`, {
		params: before != null ? { before } : undefined,
	})
	return data
}

/** Stores photos in the trip's Chat, unclaimed; a send then names them by id. */
export const uploadPhotos = async (tripId: string, photos: Blob[]): Promise<PostChatPhotosResponse> => {
	const form = new FormData()
	photos.forEach((photo, index) => form.append('photos', photo, `photo-${index + 1}.jpg`))
	const { data } = await api.post<PostChatPhotosResponse>(`${chat(tripId)}/photos`, form, {
		headers: { 'Content-Type': 'multipart/form-data' },
		timeout: 120000,
	})
	return data
}

/** Sending again with the same client id returns the Message the first send made. */
export const postMessage = async (tripId: string, body: PostChatMessageBody): Promise<ChatMessage> => {
	const { data } = await api.post<ChatMessage>(`${chat(tripId)}/messages`, body)
	return data
}

/** Returns the placeholder the stream now shows in the Message's place. */
export const deleteMessage = async (tripId: string, messageId: number): Promise<ChatMessage> => {
	const { data } = await api.delete<ChatMessage>(`${chat(tripId)}/messages/${messageId}`)
	return data
}

/** Sets my reaction, or takes it off with null; returns the Message as the server now counts it. */
export const setReaction = async (
	tripId: string,
	messageId: number,
	reaction: ChatReactionChoice | null,
): Promise<ChatMessage> => {
	const url = `${chat(tripId)}/messages/${messageId}/reaction`
	const { data } = reaction
		? await api.put<ChatMessage>(url, { reaction, analytics: getDeviceContext() })
		: await api.delete<ChatMessage>(url)
	return data
}

/** Makes the Message's link a trip Idea credited to me; returns the Message, now tagging it. */
export const addLinkAsIdea = async (tripId: string, messageId: number): Promise<ChatMessage> => {
	const { data } = await api.post<ChatMessage>(`${chat(tripId)}/messages/${messageId}/link/idea`, {
		analytics: getDeviceContext(),
	})
	return data
}

export const fetchUnread = async (tripId: string): Promise<GetChatUnreadResponse> => {
	const { data } = await api.get<GetChatUnreadResponse>(`${chat(tripId)}/unread`)
	return data
}

/** Marks the Chat read up to and including a Message; returns my unread count after it. */
export const markRead = async (tripId: string, messageId: number): Promise<GetChatUnreadResponse> => {
	const { data } = await api.put<GetChatUnreadResponse>(`${chat(tripId)}/read`, { messageId })
	return data
}

export const fetchMute = async (tripId: string): Promise<GetChatMuteResponse> => {
	const { data } = await api.get<GetChatMuteResponse>(`${chat(tripId)}/mute`)
	return data
}

export const setMute = async (tripId: string, isMuted: boolean): Promise<GetChatMuteResponse> => {
	const { data } = await api.put<GetChatMuteResponse>(`${chat(tripId)}/mute`, { isMuted })
	return data
}

/** A link in a Message I'm writing, unfurled by the server since the browser can't fetch another site. */
export const fetchLinkPreview = async (tripId: string, url: string): Promise<GetChatLinkPreviewResponse> => {
	const { data } = await api.get<GetChatLinkPreviewResponse>(`${chat(tripId)}/link-preview`, { params: { url } })
	return data
}

/** Adds me to every leg of a flight booking, or takes me off; returns the booking as it is now. */
export const setFlightPassenger = async (
	tripId: string,
	flightGroupId: number,
	isJoining: boolean,
): Promise<GetFlightGroupResponse> => {
	const { data } = await api.post<GetFlightGroupResponse>(
		`/trips/${tripId}/flights/flightGroups/${flightGroupId}/${isJoining ? 'join' : 'leave'}`,
		{ source: 'detail', analytics: getDeviceContext() },
	)
	return data
}

/** Adds me to an accommodation's guests, or takes me off; returns it as it is now. */
export const setAccommodationGuest = async (
	tripId: string,
	accommodationId: number,
	isJoining: boolean,
): Promise<GetAccommodationResponse> => {
	const { data } = await api.post<GetAccommodationResponse>(
		`/trips/${tripId}/accommodations/${accommodationId}/${isJoining ? 'join' : 'leave'}`,
		{ source: 'detail', analytics: getDeviceContext() },
	)
	return data
}
